// Upload a missing task report into that clinic's Drive folder.
// Same wire as NHP and idea files: bytes in the body, the name in a header.
import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';
import { authedWrite } from './apiClient';

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface UploadedTaskReport {
  name: string;
  url: string;
}

export interface UploadTaskReportResult {
  file: UploadedTaskReport;
  report_slot: string | null;
  taskStatus?: { id?: number; report_url?: string | null } | null;
}

export async function uploadTaskReport(
  definitionId: number,
  file: File,
): Promise<UploadTaskReportResult> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }

  const token = authToken.value;
  const res = await fetch(
    `${API_BASE}${withViewAs(`/api/v1/task-definitions/${definitionId}/report-file`)}`,
    {
      method: 'POST',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
        'idempotency-key': crypto.randomUUID(),
      },
      body: file,
    },
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  return body as UploadTaskReportResult;
}

// --- Company → Reports: a report folder's files (N-071, owner 2026-10-11) ---
//
// Add a file to the folder on screen (POST /api/v1/reports/folder/files —
// the same Drive write a task's report upload uses), or move one into that
// folder's "Old reports" (owner/director only). Nothing here deletes or
// trashes a Drive file; nothing here downloads one (N-073).

export interface ReportFolderTarget {
  clinic: string;
  group: string;
  label: string;
}

export async function uploadReportFolderFile(
  target: ReportFolderTarget,
  file: File,
  key: string = crypto.randomUUID(),
): Promise<{ file: UploadedTaskReport; folderName?: string | null }> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs('/api/v1/reports/folder/files')}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-location': target.clinic,
      'x-report-group': encodeURIComponent(target.group),
      'x-report-label': encodeURIComponent(target.label),
      'idempotency-key': key,
    },
    body: file,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  return body as { file: UploadedTaskReport; folderName?: string | null };
}

/** Only the owner or director, never while viewing as a clinic — the server
 * decides for real (src/report_files.js canMoveReportToOld). */
export function canMoveReportFiles(
  me: { role?: string | null; viewingAs?: boolean } | null | undefined,
): boolean {
  if (!me || me.viewingAs) return false;
  return me.role === 'OWNER' || me.role === 'DIRECTOR';
}

/** A manager writes their own clinic; owner and director any. Display only. */
export function canAddReportFiles(
  me: { role?: string | null; mapped?: boolean; locations?: string[] } | null | undefined,
  clinic: string,
): boolean {
  if (!me || !me.mapped) return false;
  if (me.role === 'OWNER' || me.role === 'DIRECTOR') return true;
  return (me.locations ?? []).includes(clinic);
}

/** Move one file into the folder's "Old reports" — a Drive move, never a
 * trash. The server only acts on a file that is in THIS report folder. */
export function moveReportFileToOld(
  target: ReportFolderTarget,
  fileId: string,
  key: string = crypto.randomUUID(),
): Promise<{ moved: boolean; oldFolderUrl?: string | null; alreadyMoved?: boolean }> {
  return authedWrite(
    'POST',
    `/api/v1/reports/folder/files/${encodeURIComponent(fileId)}/move-to-old`,
    { locationCode: target.clinic, group: target.group, label: target.label },
    key,
  );
}
